"use client";

import { images } from "@/lib/images";

const SERVICES = [
  {
    icon: "🏛",
    title: "Architecture d'exception",
    description:
      "Façade Haussmann, marbre veiné, verrière Belle Époque et laiton poli. Une scénographie signée SENE & DIA qui célèbre le savoir-faire français et l'élégance parisienne.",
  },
  {
    icon: "🛎",
    title: "Service de conciergerie",
    description:
      "Voiturier, porteur personnel, conseillers polyglotes, emballage cadeau sur-mesure. Le luxe d'un grand hôtel, au cœur de vos courses.",
  },
  {
    icon: "🍷",
    title: "Cave & Dégustation",
    description:
      "Une cave de 800 références, animée par un maître sommelier. Dégustations privées chaque jeudi soir, accords mets & vins sur réservation.",
  },
  {
    icon: "🧸",
    title: "Espace Famille & Kids",
    description:
      "Espace de jeux surveillé, ateliers cuisine enfants le mercredi, promenade en petit train à l'intérieur du magasin. Hyper OR I pense aux plus jeunes.",
  },
];

export function ExperienceSection() {
  return (
    <section id="experience" className="relative bg-cream py-24 md:py-32 border-t border-ink/8">
      <div className="mx-auto max-w-7xl px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="ornament-line mb-6 justify-center">
            <span className="text-gold text-[11px] tracking-[0.35em] uppercase">
              L&apos;Expérience Hyper OR
            </span>
          </div>
          <h2 className="font-display text-ink text-4xl md:text-6xl leading-[1.05]">
            Un grand magasin,
            <br />
            pas seulement
            <span className="gold-text italic"> un supermarché.</span>
          </h2>
          <p className="mt-6 text-ink/65 font-serif text-lg leading-relaxed">
            Hyper OR I réinvente la grande distribution française. Nous avons
            imaginé un lieu où le marbre rencontre l&apos;or, où le terroir
            français dialogue avec les plus belles maisons du monde. Chaque
            visite devient une expérience sensorielle, conviviale et précieuse.
          </p>
          <a
            href="#about"
            className="mt-8 inline-flex items-center gap-3 px-8 py-3.5 rounded-full gold-gradient text-ink font-semibold tracking-[0.2em] uppercase text-sm gold-shadow hover:scale-[1.02] active:scale-[0.98] transition-transform"
          >
            Découvrir la Maison SENE &amp; DIA
          </a>
        </div>

        {/* Central feature — Allée Centrale + Témoignage */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-12">
          {/* Allée Centrale */}
          <div className="relative overflow-hidden rounded-2xl bg-ink text-cream p-8 md:p-10 min-h-[320px] flex flex-col justify-between">
            <div className="absolute inset-0 ink-gradient opacity-90"></div>
            {images["experience:nave"] && (
              <img
                src={images["experience:nave"] as string}
                alt="Allée Centrale — La nef de marbre blanc"
                className="absolute inset-0 w-full h-full object-cover opacity-50"
                loading="lazy"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40"></div>
            <div
              className="absolute inset-0 opacity-30"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 30% 40%, rgba(200,162,74,0.6) 0%, transparent 60%)",
              }}
            ></div>
            <div className="relative">
              <span className="text-gold text-[11px] tracking-[0.3em] uppercase">
                Allée Centrale
              </span>
              <h3 className="mt-3 font-display text-cream text-3xl md:text-4xl leading-tight">
                La nef de marbre blanc
              </h3>
            </div>
            <div className="relative mt-8">
              <div className="font-display text-gold text-5xl">12 K</div>
              <div className="text-cream/60 text-[11px] tracking-[0.25em] uppercase mt-2">
                m² de marbre
              </div>
              <p className="mt-2 text-cream/55 font-serif text-sm">
                italien posé à la main
              </p>
            </div>
          </div>

          {/* Témoignage client */}
          <div className="relative overflow-hidden rounded-2xl bg-parchment p-8 md:p-10 min-h-[320px] flex flex-col justify-between">
            <div>
              <span className="text-gold text-3xl font-display">“</span>
              <p className="mt-3 font-serif italic text-ink text-xl md:text-2xl leading-relaxed">
                « Hyper OR I n&apos;est pas un supermarché. C&apos;est un lieu
                de vie où l&apos;on vient flâner en famille, découvrir, se faire
                plaisir. Le service est irréprochable, l&apos;atmosphère
                princière. »
              </p>
            </div>
            <div className="mt-6 pt-6 border-t border-ink/10">
              <div className="font-display text-ink text-base">
                — Aïssatou Diallo
              </div>
              <div className="text-ink/55 text-[11px] tracking-[0.2em] uppercase mt-1">
                Cliente OR Premium
              </div>
            </div>
          </div>
        </div>

        {/* 4 service cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {SERVICES.map((s, idx) => (
            <article
              key={idx}
              className="group relative bg-white border border-ink/8 rounded-xl p-6 hover:luxury-shadow transition-all duration-500 hover:-translate-y-0.5"
            >
              <div className="text-3xl mb-4">{s.icon}</div>
              <h3 className="font-display text-ink text-xl leading-tight">
                {s.title}
              </h3>
              <p className="mt-3 text-ink/60 text-[13px] leading-relaxed font-serif">
                {s.description}
              </p>
              <div className="mt-4 inline-flex items-center gap-2 text-gold text-[10px] tracking-[0.25em] uppercase border-b border-gold/40 pb-0.5 group-hover:gap-3 transition-all">
                En savoir plus
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
